<template>
  <!-- Tawk.to 위젯은 스크립트에 의해 동적으로 body에 추가되므로, -->
  <!-- 이 컴포넌트의 템플릿은 주로 로딩/에러 상태 표시에 사용됩니다. -->
  <div class="tawkto-chat-container">
    <div v-if="!configLoaded && !initializationError" class="tawkto-status">
      <p>Tawk.to 설정을 불러오는 중입니다...</p>
      <!-- 필요시 로딩 스피너 등 추가 -->
    </div>
    <div v-if="initializationError" class="tawkto-status tawkto-error d-none">
      <p>Tawk.to 를 로드하는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.</p>
      <p v-if="lastErrorMessage" style="font-size: 0.8em; color: #a94442;">오류: {{ lastErrorMessage }}</p>
    </div>
    <!-- 로딩 성공 시 위젯이 표시되므로 이 컴포넌트 자체는 빈 공간일 수 있습니다. -->
    <!-- 또는 위젯의 placeholder 역할만 합니다. -->
  </div>
</template>

<script>
import axios from 'axios'; // HTTP 요청 라이브러리

// 페이지 새로고침 없이 컴포넌트가 재마운트될 경우 스크립트 중복 로드를 방지하기 위한 플래그
let tawkScriptLoaded = false;
// Tawk_API 로딩 완료 콜백이 여러 번 등록되는 것을 방지 (혹시 모를 상황 대비)
let tawkOnLoadCallbackSet = false;

export default {
  name: "TawkToChat",
  data() {
    return {
      // 백엔드로부터 받을 설정 정보
      config: {
        propertyId: '',
        widgetId: '',
        hash: '' // Secure Mode 인증용 해시
      },
      // 상태 추적 변수
      configLoaded: false,      // 백엔드 설정 로드 완료 여부
      isTawkApiReady: false,    // Tawk_API 객체 사용 준비 완료 여부
      initializationError: false, // 초기화 과정 중 오류 발생 여부
      lastErrorMessage: '', // 마지막 에러 메시지 저장
      isLoggedInToTawk: false // ★★★ Tawk.to에 성공적으로 로그인했는지 추적하는 플래그
    };
  },
  computed: {
    // Vuex 스토어에서 사용자 정보를 가져옵니다.
    // 실제 스토어 구조에 맞게 경로를 수정해야 할 수 있습니다.
    userInfo() {
      // 사용자가 로그인하지 않았을 경우를 대비하여 기본값으로 빈 객체 제공
      return this.$store.state.user || {};
    }
  },
  mounted() {
    console.log("TawkToChat: Component mounted.");
    // 컴포넌트 마운트 시 사용자 정보가 있으면 설정 로드 시작
    if (this.userInfo && this.userInfo.companyid) {
      console.log("TawkToChat: User info available on mount, fetching config.");
      this.fetchTawkConfig();
    } else {
      // 사용자 정보가 아직 로드되지 않았을 수 있음 (비동기 로그인 등)
      console.warn("TawkToChat: User info not available immediately on mount. Waiting for watch update.");
    }
  },
  beforeDestroy() {
    // 컴포넌트 소멸 시 콜백 플래그 리셋
    console.log("TawkToChat: Component beforeDestroy.");
    tawkOnLoadCallbackSet = false;
    // 컴포넌트 소멸 시 로그인 상태 리셋 (선택적이지만 안전)
    this.isLoggedInToTawk = false;
  },
  methods: {
    /**
     * 백엔드 API 서버로부터 Tawk.to 설정값 (propertyId, widgetId, hash)을 가져옵니다.
     */
    fetchTawkConfig() {
      console.log("TawkToChat: Fetching Tawk configuration from backend...");
      this.initializationError = false; // 이전 오류 상태 초기화
      this.lastErrorMessage = ''; // 에러 메시지 초기화
      this.isLoggedInToTawk = false; // ★ 설정 다시 로드 시 Tawk 로그인 상태 리셋

      // 서버 API 엔드포인트 및 요청 방식 확인 (POST 가정)
      console.log("TawkToChat: Requesting config for userId:", this.userInfo.companyid);

      // 서명할 회사는 서버가 세션으로 정한다 — body 로 안 보냄(cpl-detail-keyleft-1)
      axios.post('/api/tawk-config', {})
        .then(response => {
          console.log("TawkToChat: Config response received:", response.data);
          const { propertyId, widgetId, hash } = response.data;

          // 응답 데이터 유효성 검사 (hash 포함)
          if (!propertyId || !widgetId || !hash) {
            console.error("TawkToChat: Incomplete Tawk config received from backend:", response.data);
            this.initializationError = true;
            this.lastErrorMessage = '서버로부터 유효한 설정을 받지 못했습니다.';
            return;
          }

          // 설정값 및 상태 업데이트 (원래 성공했던 방식)
          this.config.propertyId = propertyId;
          this.config.widgetId = widgetId;
          this.config.hash = hash; // Secure Mode 해시 다시 추가
          this.configLoaded = true;
          console.log("TawkToChat: Tawk config loaded successfully.");

          // 설정 로드 완료 후 Tawk.to 스크립트 로드/초기화 시도
          this.loadTawkToScript();
        })
        .catch(error => {
          // 세션이 끊긴 채 열린 화면 — 상담창을 띄우지 않고 조용히(오류 창 0)
          if (error.response && error.response.status === 401) {
            console.warn("TawkToChat: 로그인 세션 없음(401) — 상담창 안 띄움");
            return;
          }
          const errMsg = error.response?.data?.error || error.message || '알 수 없는 오류';
          console.error("TawkToChat: Error fetching Tawk config:", errMsg, error);
          this.initializationError = true; // API 호출 실패 시 에러 상태 설정
          this.lastErrorMessage = `설정 로드 실패: ${errMsg}`;
        });
    },

    /**
     * Tawk.to 임베드 스크립트를 동적으로 로드하고 초기화합니다.
     */
    loadTawkToScript() {
      // 설정 로드 전이면 중단
      if (!this.configLoaded) {
        console.warn("TawkToChat: Cannot load script, config not loaded yet.");
        return;
      }
      // 스크립트가 이미 페이지에 추가되었다면
      if (tawkScriptLoaded) {
        console.log("TawkToChat: Tawk script already added to the page.");
        // API가 준비되었고 아직 Tawk에 로그인하지 않았다면 로그인 시도
        if (this.isTawkApiReady && !this.isLoggedInToTawk) {
          console.log("TawkToChat: Script already loaded, API ready, attempting login.");
          this.loginUser();
        }
        // API 준비 콜백이 아직 설정 안된 극히 드문 경우 대비
        else if (!this.isTawkApiReady && window.Tawk_API && typeof window.Tawk_API.onLoad === 'function' && !tawkOnLoadCallbackSet) {
           this.setupApiCallbacks();
           console.log("TawkToChat: Script was added, setting up callbacks again just in case.");
        }
        return; // 스크립트 로드는 더 이상 진행하지 않음
      }

      console.log("TawkToChat: Loading Tawk.to script with Property ID:", this.config.propertyId, "Widget ID:", this.config.widgetId);

      // Tawk_API 객체 초기화 (스크립트 로드 전에 정의해야 함)
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date(); // 로드 시간 측정 시작

      // API 콜백 설정 (한 번만 설정하도록 플래그 사용)
      this.setupApiCallbacks();

      // 스크립트 태그 동적 생성 및 문서에 삽입
      try {
        const script = document.createElement("script");
        script.id = "tawkto-script"; // 중복 확인용 ID
        script.async = true;
        script.src = `https://embed.tawk.to/${this.config.propertyId}/${this.config.widgetId}`;
        script.charset = 'UTF-8';
        script.setAttribute('crossorigin', '*');

        // 스크립트 태그 자체의 로드 실패 처리 (네트워크 오류 등)
        script.onerror = (event) => {
          console.error("TawkToChat: Failed to load the Tawk.to script tag. Check network connection, ad blockers, or CSP.", event);
          this.initializationError = true;
          this.lastErrorMessage = 'Tawk.to 스크립트 로드 실패 (네트워크 확인 필요)';
          // window.Tawk_API.onError 콜백이 호출되지 않을 수 있으므로 여기서도 에러 처리
          if (typeof window.Tawk_API?.onError === 'function') {
              window.Tawk_API.onError({ type: 'script_tag_error', event: event });
          }
        };

        // 문서 내 첫 번째 script 태그 앞에 삽입 (표준 방식)
        const firstScript = document.getElementsByTagName("script")[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
            tawkScriptLoaded = true; // 스크립트 추가 완료 플래그 설정
            console.log("TawkToChat: Tawk script tag added to the document.");
        } else {
            // script 태그를 찾을 수 없는 극단적인 경우 body 끝에 추가
            document.body.appendChild(script);
            tawkScriptLoaded = true;
             console.warn("TawkToChat: No existing script tag found, appended Tawk script to body.");
        }
      } catch (e) {
          console.error("TawkToChat: Error occurred while creating or appending Tawk script tag:", e);
          this.initializationError = true;
          this.lastErrorMessage = '스크립트 태그 생성/추가 오류';
      }
    },

    /**
     * window.Tawk_API 객체에 필요한 콜백 함수들을 설정합니다.
     * 중복 설정을 방지하기 위해 플래그(tawkOnLoadCallbackSet)를 사용합니다.
     */
    setupApiCallbacks() {
        if (tawkOnLoadCallbackSet) {
            console.log("TawkToChat: Callbacks already set, skipping setup.");
            return; // 이미 콜백이 설정되었다면 중복 설정 방지
        }

        console.log("TawkToChat: Setting up Tawk_API callbacks (onLoad, onError).");

        // onLoad 콜백: 스크립트 로드 및 API 준비 완료 시 호출됨
        window.Tawk_API.onLoad = () => {
            console.log("TawkToChat: Tawk script loaded and API ready (via onLoad callback).");
            this.isTawkApiReady = true; // API 준비 완료 상태 업데이트
            // ★ onLoad 시점에 아직 Tawk에 로그인하지 않은 경우에만 로그인 시도
            if (!this.isLoggedInToTawk) {
                console.log("TawkToChat: API ready, initiating login process.");
                this.loginUser();
            } else {
                 console.log("TawkToChat: API ready, but already logged in to Tawk.");
            }
        };

        // onError 콜백: Tawk.to 스크립트 초기화 중 내부 오류 발생 시 호출됨
        window.Tawk_API.onError = (error) => {
            console.error("TawkToChat: An error occurred within Tawk.to script initialization:", error);
            this.initializationError = true; // 내부 오류 발생 시 에러 상태 설정
            this.lastErrorMessage = `Tawk.to 초기화 오류: ${error?.message || error}`;
        };

        tawkOnLoadCallbackSet = true; // 콜백 설정 완료 플래그
    },

    /**
     * Tawk.to Secure Mode를 사용하여 사용자를 로그인시킵니다.
     * 이 메소드는 Tawk_API가 준비된 후 (onLoad 콜백 내 또는 이후) 호출되어야 합니다.
     */
    loginUser() {
      // API가 준비되었는지, login 함수가 존재하는지 확인
      if (!this.isTawkApiReady || typeof window.Tawk_API?.login !== 'function') {
        console.warn("TawkToChat: Attempted to login, but Tawk_API or login function is not ready yet.");
        return;
      }

      // Secure Mode - 사용자 정보 및 해시값 확인 (원래 성공했던 방식)
      if (!this.userInfo || !this.userInfo.companyid || !this.config.hash) {
        console.warn("TawkToChat: Cannot login because user info (companyid) or secure hash is missing.", {
            hasUserInfo: !!this.userInfo,
            hasCompanyId: !!this.userInfo?.companyid,
            hasHash: !!this.config.hash
        });
        this.initializationError = true;
        this.lastErrorMessage = '로그인 실패: 사용자 ID 또는 해시 정보가 없습니다.';
        return;
      }

      // ★ 이미 성공적으로 로그인했다면 중복 호출 방지
      if (this.isLoggedInToTawk) {
          console.log("TawkToChat: Already logged in to Tawk. Skipping redundant login call.");
          return;
      }

      console.log("TawkToChat: Attempting Tawk.to login using Secure Mode.");

      const userIdToSend = String(this.userInfo.companyid); // 문자열로 변환
      const nameToSend = this.userInfo.name || userIdToSend; // 이름 없으면 ID 사용

      // 이메일 검증 및 기본값 설정
      let emailToSend = this.userInfo.email;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailToSend || !emailRegex.test(emailToSend)) {
        // 이메일이 없거나 형식이 올바르지 않으면 기본 이메일 생성
        emailToSend = `${userIdToSend}@couplus.com`;
        console.log("TawkToChat: Invalid or missing email, using default:", emailToSend);
      }

      // 전화번호 완전 제거 (Tawk.to 호환성 문제로 인해)
      let phoneToSend = undefined;
      console.log("TawkToChat: Phone field completely removed for Tawk.to compatibility");

      console.log("TawkToChat: Login data:", {
        userId: userIdToSend,
        name: nameToSend,
        email: emailToSend,
        phone: phoneToSend,
        hasHash: !!this.config.hash
      });

      // 간단하게 name만 사용
      const loginData = {
        userId: userIdToSend,              // 해시 검증용: 원본 "test2" 사용
        hash: this.config.hash,            // required (원본 userId로 생성된 해시)
        name: nameToSend,                  // 표시용: 그냥 "test2"로 표시
        email: emailToSend,                // optional
      };

      // 유효한 전화번호가 있는 경우에만 추가
      if (phoneToSend) {
        loginData.phone = phoneToSend;
      }

      // 콘솔에 전송될 데이터 로깅
      console.log("TawkToChat: Data being sent to Tawk_API.login:", JSON.stringify(loginData, null, 2));

      // Tawk_API.login 호출 (원래 성공했던 방식)
      console.log("TawkToChat: Calling Tawk_API.login with data:", JSON.stringify(loginData, null, 2));

      window.Tawk_API.login(loginData, (error) => {
        if (error) {
          // 오류 발생 시 처리
          console.error("TawkToChat: Error during Tawk_API.login (Secure Mode login failed):");
          console.error("TawkToChat: Error details:", error);
          console.error("TawkToChat: Error type:", typeof error);
          console.error("TawkToChat: Error JSON:", JSON.stringify(error, null, 2));

          this.initializationError = true;
          this.lastErrorMessage = `Secure Mode 로그인 실패: ${error.code || error.message || JSON.stringify(error)}`;
          this.isLoggedInToTawk = false;
        } else {
          // 성공 시 처리 (setAttributes 제거 - 원래 성공했던 방식)
          console.log("TawkToChat: Tawk_API.login successful (Secure Mode login successful).");
          this.initializationError = false;
          this.lastErrorMessage = '';
          this.isLoggedInToTawk = true;

          console.log("TawkToChat: Login completed successfully without setAttributes");
        }
      });
    },

    // setJobTitle 메서드 제거 - 원래 성공했던 방식에서는 사용하지 않음
  },
  watch: {
    // Vuex 스토어의 사용자 정보 변경을 감지 (비동기 로그인 완료, 사용자 변경, 로그아웃 등)
    userInfo: {
      handler(newUserInfo, oldUserInfo) {
        console.log("TawkToChat: Watcher detected userInfo change.");
        const wasLoggedIn = oldUserInfo && oldUserInfo.companyid;
        const isLoggedIn = newUserInfo && newUserInfo.companyid;

        // --- 케이스 1: 사용자가 새로 로그인했거나 변경된 경우 ---
        if (isLoggedIn && (!wasLoggedIn || newUserInfo.companyid !== oldUserInfo?.companyid)) {
           console.log("TawkToChat: User logged in or changed. Processing Tawk integration.");

           // 1a. 사용자 ID가 변경되었거나, Vuex 상태에 처음으로 유효한 사용자 정보가 들어온 경우
           if (!wasLoggedIn || newUserInfo.companyid !== oldUserInfo?.companyid) {
              console.log("TawkToChat: User ID changed or initial valid user info detected, fetching/re-fetching config.");
              this.isLoggedInToTawk = false; // ★ ID 변경 시 Tawk 로그인 상태 리셋
              this.fetchTawkConfig(); // 설정 다시 로드 -> 성공 시 스크립트 로드 -> API 준비 후 로그인
           }
           // 1b. 사용자 ID는 동일하지만, 다른 정보(이름/이메일 등)만 변경되었고, *아직 설정 로드가 안된 경우* (가능성 낮음)
           else if (!this.configLoaded) {
              console.log("TawkToChat: Config not loaded yet, fetching now.");
              this.fetchTawkConfig();
           }
           // 1c. 사용자 ID는 동일, 다른 정보 변경, 설정 로드 완료, API 준비 완료, *하지만 Tawk 로그인은 아직 안된 경우*
           else if (this.isTawkApiReady && !this.isLoggedInToTawk) {
               console.log("TawkToChat: User info updated, API ready but not logged in yet. Attempting login.");
               this.loginUser(); // 로그인 시도
           }
           // 1d. 사용자 ID는 동일, 다른 정보 변경, 설정 로드 완료, API 준비 완료, *이미 Tawk 로그인 된 경우*
           else if (this.isTawkApiReady && this.isLoggedInToTawk) {
               console.log("TawkToChat: User info (not ID) updated, already logged in. Tawk might update automatically or consider setAttributes if needed.");
               // 여기서 loginUser()를 다시 호출하지 않음. 필요 시 setAttributes로 업데이트 고려.
               // 예시: window.Tawk_API.setAttributes({ name: newUserInfo.name, email: newUserInfo.email }, err => {});
           }
        }
        // --- 케이스 2: 사용자가 로그아웃한 경우 ---
        else if (wasLoggedIn && !isLoggedIn) {
           console.log("TawkToChat: User logged out.");
           this.isLoggedInToTawk = false; // ★ 로그아웃 시 Tawk 로그인 상태 리셋
           // 필요 시 추가적인 로그아웃 처리 로직 (예: 채팅 종료)
           // if (window.Tawk_API && typeof window.Tawk_API.endChat === 'function') {
           //    window.Tawk_API.endChat();
           // }
        }
      },
      deep: true // 중첩된 객체(userInfo) 내부의 변경까지 감지
    }
  }
};
</script>

<style scoped>
/* 컴포넌트 자체의 스타일 */
.tawkto-chat-container {
  /* 필요에 따라 컨테이너 스타일 지정 (예: 최소 높이) */
}

/* 로딩 또는 에러 메시지 스타일 */
.tawkto-status {
  padding: 10px;
  margin: 5px 0;
  text-align: center;
  font-size: 0.9em;
  color: #555;
  background-color: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 4px;
}

.tawkto-error {
  background-color: #f2dede;
  border-color: #ebccd1;
}

.tawkto-error p {
  color: #a94442; /* 에러 메시지는 붉은색으로 강조 */
}
</style>